import {
  ChevronRight,
  ScanLine,
  ArrowLeftRight,
  Truck,
  Wrench,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { T } from "../theme"
import {
  Avatar,
  MCard,
  MStageBadge,
  SectionLabel,
  Touchable,
} from "../primitives"
import { timeAgo } from "../utils"
import type { MobileAsset, MobileEvent, MobileUser, StageStat } from "../types"

interface HomeScreenProps {
  user: MobileUser
  totalAssets: number
  stageStats: StageStat[]
  highlights: { label: string; value: number; stat?: StageStat; sub?: string; emphasis?: boolean }[]
  recentActivity: MobileEvent[]
  onNavigate: (tab: "scan" | "inventory" | "activity") => void
  onOpenAsset: (asset: Pick<MobileAsset, "id" | "code">) => void
}

function partOfDayGreeting(t: TFunction) {
  const h = new Date().getHours()
  if (h < 12) return t("mobile.greetings.morning")
  if (h < 18) return t("mobile.greetings.afternoon")
  return t("mobile.greetings.evening")
}

interface HomeHeaderProps {
  user: MobileUser
}

function HomeHeader({ user }: HomeHeaderProps) {
  const { t } = useTranslation()
  const firstName = (user.name || "").split(" ")[0] || user.name
  return (
    <div style={{ padding: "16px 20px 8px", background: T.surface }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: T.fontBody, fontSize: 13, color: T.inkSoft }}>
            {partOfDayGreeting(t)},
          </div>
          <div
            style={{
              fontFamily: T.fontDisplay,
              fontSize: 22,
              fontWeight: 700,
              color: T.ink,
              letterSpacing: -0.4,
              marginTop: 2,
            }}
          >
            {firstName}
          </div>
        </div>
        <Avatar name={user.name} size={40} />
      </div>
    </div>
  )
}

interface ScanCtaProps {
  onClick: () => void
}

function ScanCta({ onClick }: ScanCtaProps) {
  const { t } = useTranslation()
  return (
    <Touchable
      onClick={onClick}
      style={{
        background: T.ink,
        color: "#fff",
        borderRadius: T.radius,
        padding: 20,
        display: "flex",
        alignItems: "center",
        gap: 16,
        marginBottom: 24,
      }}
    >
      <div
        style={{
          width: 56, height: 56, background: T.accent, color: "#fff",
          display: "grid", placeItems: "center", flexShrink: 0,
        }}
      >
        <ScanLine size={28} color="#fff" strokeWidth={2} />
      </div>
      <div
        style={{
          flex: 1,
          fontFamily: T.fontDisplay,
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: -0.2,
          textTransform: "uppercase",
        }}
      >
        {t("mobile.home.scanCta")}
      </div>
      <ChevronRight size={20} color="#fff" />
    </Touchable>
  )
}

export function HomeScreen({
  user,
  totalAssets,
  stageStats,
  highlights,
  recentActivity,
  onNavigate,
  onOpenAsset,
}: HomeScreenProps) {
  const { t } = useTranslation()
  const totalForBar = stageStats.reduce((s, x) => s + x.count, 0) || 1

  return (
    <div>
      <HomeHeader user={user} />

      <div style={{ padding: "20px 16px 100px" }}>
        <ScanCta onClick={() => onNavigate("scan")} />

        {/* Stat highlights */}
        <SectionLabel>{t("mobile.home.overview")}</SectionLabel>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            marginBottom: 24,
          }}
        >
          <StatCard
            label={t("mobile.home.total")}
            value={totalAssets}
            sub={t("mobile.home.assetsSuffix")}
            emphasis
          />
          {highlights.slice(0, 3).map((h) => (
            <StatCard
              key={h.label}
              label={h.label}
              value={h.value}
              sub={h.sub ?? t("mobile.home.assetsSuffix")}
              accent={h.stat?.stage.color}
              emphasis={h.emphasis}
            />
          ))}
        </div>

        {/* Distribution by stage */}
        <SectionLabel
          action={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              {t("mobile.home.viewAll")} <ChevronRight size={12} />
            </span>
          }
          onAction={() => onNavigate("inventory")}
        >
          {t("mobile.home.stageDistribution")}
        </SectionLabel>
        <MCard style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              height: 12,
              overflow: "hidden",
              marginBottom: 14,
              border: `1px solid ${T.border}`,
            }}
          >
            {stageStats.map((s, i) => (
              <div
                key={s.stage.id}
                style={{
                  flex: s.count || 0.0001,
                  background: s.stage.color,
                  borderRight:
                    i === stageStats.length - 1 ? "none" : `1px solid ${T.surface}`,
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {stageStats.map((s) => (
              <div key={s.stage.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 10, height: 10, background: s.stage.color }} />
                <span style={{ flex: 1, fontFamily: T.fontBody, fontSize: 14, color: T.ink }}>
                  {s.stage.name}
                </span>
                <span style={{ fontFamily: T.fontMono, fontSize: 11.5, color: T.inkSoft }}>
                  {Math.round((s.count / totalForBar) * 100)}%
                </span>
                <span
                  style={{ fontFamily: T.fontMono, fontSize: 13, fontWeight: 700, color: T.ink, minWidth: 32, textAlign: "right" }}
                >
                  {String(s.count).padStart(3, "0")}
                </span>
              </div>
            ))}
          </div>
        </MCard>

        {/* Recent activity */}
        <SectionLabel
          action={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              {t("mobile.home.viewAll")} <ChevronRight size={12} />
            </span>
          }
          onAction={() => onNavigate("activity")}
        >
          {t("mobile.home.recentActivity")}
        </SectionLabel>
        <MCard padded={false}>
          {recentActivity.length === 0 ? (
            <div
              style={{
                padding: "32px 20px",
                textAlign: "center",
                color: T.inkSoft,
                fontSize: 14,
              }}
            >
              {t("mobile.home.noRecentActivity")}
            </div>
          ) : (
            recentActivity
              .slice(0, 3)
              .map((a, i, arr) => (
                <ActivityRow
                  key={a.id ?? i}
                  event={a}
                  isLast={i === arr.length - 1}
                  onClick={
                    a.asset_id
                      ? () => onOpenAsset({ id: a.asset_id!, code: a.asset_code || "" })
                      : undefined
                  }
                />
              ))
          )}
        </MCard>
      </div>
    </div>
  )
}

interface StatCardProps {
  label: string
  value: number | string
  sub?: string
  accent?: string
  emphasis?: boolean
}

function StatCard({ label, value, sub, accent, emphasis }: StatCardProps) {
  const railColor = emphasis ? T.ink : accent || T.ink
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        padding: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: 3, background: railColor,
        }}
      />
      <div
        style={{
          fontFamily: T.fontMono,
          fontSize: 10,
          color: T.inkSoft,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: T.fontDisplay,
          fontSize: 32,
          fontWeight: 700,
          color: T.ink,
          marginTop: 4,
          letterSpacing: -0.8,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontFamily: T.fontMono,
            fontSize: 10.5,
            color: emphasis ? T.accent : T.inkSoft,
            marginTop: 6,
            letterSpacing: 0.4,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  )
}

interface ActivityRowProps {
  event: MobileEvent
  isLast?: boolean
  onClick?: () => void
}

const TYPE_ICON: Record<string, { Icon: LucideIcon; bg: string; ink: string }> = {
  stage:       { Icon: ArrowLeftRight, bg: T.accentSoft, ink: T.accent },
  move:        { Icon: Truck,          bg: T.warningSoft, ink: T.warning },
  maintenance: { Icon: Wrench,         bg: T.surfaceAlt, ink: T.inkSoft },
}

export function ActivityRow({ event: e, isLast, onClick }: ActivityRowProps) {
  const { t } = useTranslation()
  const meta = TYPE_ICON[e.type] || TYPE_ICON.stage
  const Icon = meta.Icon
  const when = e.when || timeAgo(e.created_at, t)
  return (
    <Touchable
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "14px 16px",
        borderBottom: isLast ? "none" : `1px solid ${T.divider}`,
      }}
    >
      <div
        style={{
          width: 36, height: 36, flexShrink: 0,
          background: meta.bg,
          color: meta.ink,
          display: "grid", placeItems: "center",
          border: `1px solid ${meta.ink}40`,
        }}
      >
        <Icon size={17} color={meta.ink} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <span
            style={{
              fontFamily: T.fontMono,
              fontSize: 13,
              fontWeight: 700,
              color: T.ink,
              letterSpacing: 0.2,
            }}
          >
            {e.asset_code || "—"}
          </span>
          {(e.asset_product || e.asset_container) && (
            <span
              style={{
                fontSize: 12,
                color: T.inkSoft,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {[e.asset_product, e.asset_container].filter(Boolean).join(" · ")}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {e.type === "stage" && e.from_stage && e.to_stage && (
            <>
              <MStageBadge stage={e.from_stage} size="sm" variant="soft" />
              <ChevronRight size={11} color={T.inkSoft} />
              <MStageBadge stage={e.to_stage} size="sm" />
            </>
          )}
          {e.type === "move" && (e.from_location || e.to_location) && (
            <span style={{ fontSize: 12.5, color: T.inkSoft }}>
              {e.from_location?.name || "—"} →{" "}
              <span style={{ color: T.ink, fontWeight: 600 }}>{e.to_location?.name || "—"}</span>
            </span>
          )}
          {e.note && (
            <span style={{ fontSize: 12.5, color: T.inkSoft, fontStyle: "italic" }}>{e.note}</span>
          )}
        </div>
        <div
          style={{
            fontFamily: T.fontMono,
            fontSize: 10.5,
            color: T.inkMuted,
            marginTop: 6,
            letterSpacing: 0.4,
          }}
        >
          {[e.who, when].filter(Boolean).join(" · ")}
        </div>
      </div>
    </Touchable>
  )
}

interface MemberHomeScreenProps {
  user: MobileUser
  myActivity: MobileEvent[]
  onOpenScan: () => void
}

export function MemberHomeScreen({ user, myActivity, onOpenScan }: MemberHomeScreenProps) {
  const { t } = useTranslation()

  return (
    <div>
      <HomeHeader user={user} />

      <div style={{ padding: "20px 16px 100px" }}>
        <ScanCta onClick={onOpenScan} />

        <SectionLabel>{t("mobile.home.yourRecentActivity")}</SectionLabel>
        <MCard padded={false}>
          {myActivity.length === 0 ? (
            <div
              style={{
                padding: "32px 20px",
                textAlign: "center",
                color: T.inkSoft,
                fontSize: 14,
              }}
            >
              {t("mobile.home.noActivityYet")}
            </div>
          ) : (
            myActivity.map((a, i, arr) => (
              <ActivityRow
                key={a.id ?? i}
                event={a}
                isLast={i === arr.length - 1}
              />
            ))
          )}
        </MCard>
      </div>
    </div>
  )
}
